import React, { useState } from 'react';
import { 
  FileText, 
  Printer, 
  X,
  GraduationCap,
  Award,
  User,
  School,
  Calendar,
  TrendingUp,
  Settings,
  Check,
  XCircle,
  Beaker,
  Calculator,
  BookOpen
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from 'sonner';

// Stream definitions for Years 11-13
const STREAM_SUBJECTS = {
  Science: {
    core: ["English", "Mathematics", "Chemistry", "Biology", "Physics"],
    optional: ["ICT", "Agriculture", "French", "Geography"],
    maxOptional: 1,
    icon: Beaker,
    color: "bg-purple-100 text-purple-800 border-purple-300"
  },
  Commerce: {
    core: ["English", "Mathematics", "Economics", "Accounting", "ICT"],
    optional: ["French", "Agriculture"],
    maxOptional: 1,
    icon: Calculator,
    color: "bg-green-100 text-green-800 border-green-300"
  },
  Arts: {
    core: ["English", "Mathematics", "History", "Geography", "Development Studies"],
    optional: ["ICT", "Agriculture", "French"],
    maxOptional: 1,
    icon: BookOpen,
    color: "bg-amber-100 text-amber-800 border-amber-300"
  }
};

/**
 * Secondary School Report Card Component (Years 11-13)
 * Stream-based report cards with customizable subject display
 * Streams: Science, Commerce, Arts
 */
export default function SecondaryReportCard({ 
  isOpen, 
  onClose, 
  student, 
  classInfo,
  grades = [],
  schoolName = "St Patrick's College",
  term = "Term 1",
  year = 2025,
  studentYear = 11,
  teacherName,
  principalName,
  schoolLogo,
  schoolAddress = {
    poBox: "PO BOX 19",
    location: "Nawolu, Vureas, North East Ambae",
    country: "VANUATU",
    email: "spc.secretaryoffice@gmail.com"
  },
  classTeacherComment = '',
  principalComment = '',
  attendance = null,  // New: attendance data from API
  onSubjectToggle,
  onStreamChange
}) {
  const [showSettings, setShowSettings] = useState(false);
  const [manualStream, setManualStream] = useState(null);
  const [manualActiveSubjects, setManualActiveSubjects] = useState({});
  const [manualOptional, setManualOptional] = useState(null);

  // Derive initial values from grades using useMemo (not useEffect)
  const derivedValues = React.useMemo(() => {
    if (!student || grades.length === 0) {
      return { stream: 'Science', activeSubjects: {}, optional: '' };
    }
    
    const initial = {};
    grades.forEach(g => {
      initial[g.name] = g.isActive !== false;
    });
    
    const subjectNames = grades.map(g => g.name);
    let detectedStream = 'Science';
    if (subjectNames.includes('Chemistry') && subjectNames.includes('Physics')) {
      detectedStream = 'Science';
    } else if (subjectNames.includes('Economics') && subjectNames.includes('Accounting')) {
      detectedStream = 'Commerce';
    } else if (subjectNames.includes('History') && subjectNames.includes('Development Studies')) {
      detectedStream = 'Arts';
    }
    
    const streamDef = STREAM_SUBJECTS[detectedStream];
    const detectedOptional = streamDef ? (subjectNames.find(s => streamDef.optional.includes(s)) || '') : '';
    
    return { stream: detectedStream, activeSubjects: initial, optional: detectedOptional };
  }, [student, grades]);

  // Use manual overrides if set, otherwise use derived values
  const selectedStream = manualStream || derivedValues.stream;
  const activeSubjects = Object.keys(manualActiveSubjects).length > 0 ? manualActiveSubjects : derivedValues.activeSubjects;
  const selectedOptional = manualOptional !== null ? manualOptional : derivedValues.optional;

  // Handlers to update manual state
  const setSelectedStream = (stream) => setManualStream(stream);
  const setActiveSubjects = (updater) => {
    if (typeof updater === 'function') {
      setManualActiveSubjects(prev => updater(Object.keys(prev).length > 0 ? prev : derivedValues.activeSubjects));
    } else {
      setManualActiveSubjects(updater);
    }
  };
  const setSelectedOptional = (opt) => setManualOptional(opt);

  if (!student) return null;

  // Check if this is St Patrick's College to show the logo
  const isStPatricks = schoolName?.toLowerCase().includes("patrick") || 
                       schoolName?.toLowerCase().includes("spc");
  
  // Get the appropriate logo URL
  const getLogoUrl = () => {
    if (schoolLogo) return schoolLogo;
    if (isStPatricks) return "/spc-logo.png";
    return null;
  };

  const logoUrl = getLogoUrl();

  // Vanuatu Secondary School Grading Scale (matches Grade Book)
  const calculateGrade = (percentage) => {
    if (percentage >= 85) return { grade: 'A', gpa: 4.0, description: 'Excellent' };
    if (percentage >= 70) return { grade: 'B', gpa: 3.0, description: 'Good' };
    if (percentage >= 50) return { grade: 'C', gpa: 2.0, description: 'Satisfactory' };
    if (percentage >= 30) return { grade: 'D', gpa: 1.0, description: 'Below Average' };
    return { grade: 'E', gpa: 0.0, description: 'Low' };
  };

  // Point scale conversion (matches Grade Book A-E scale)
  const getPointScale = (percentage) => {
    if (percentage >= 85) return '4.0';
    if (percentage >= 70) return '3.0';
    if (percentage >= 50) return '2.0';
    if (percentage >= 30) return '1.0';
    return '0.0';
  };

  // Auto-generate subject-specific teacher comments based on performance
  const generateSubjectComment = (subjectName, percentage, studentFirstName) => {
    const gradeInfo = calculateGrade(percentage);
    const comments = {
      'A+': [
        `${studentFirstName} demonstrates exceptional mastery of ${subjectName}. Outstanding achievement!`,
        `Remarkable performance in ${subjectName}. ${studentFirstName} is a role model for peers.`,
        `${studentFirstName} shows outstanding dedication and excellence in ${subjectName}.`
      ],
      'A': [
        `Excellent work in ${subjectName}. ${studentFirstName} consistently performs at a high level.`,
        `${studentFirstName} demonstrates strong understanding and excellent effort in ${subjectName}.`,
        `Very impressive performance. ${studentFirstName} excels in all aspects of ${subjectName}.`
      ],
      'A-': [
        `Very good performance in ${subjectName}. ${studentFirstName} shows consistent effort.`,
        `${studentFirstName} demonstrates strong capabilities in ${subjectName}. Keep up the good work!`,
        `Commendable work in ${subjectName}. ${studentFirstName} is performing very well.`
      ],
      'B+': [
        `${studentFirstName} shows good progress in ${subjectName}. Continue the hard work!`,
        `Good understanding of ${subjectName} concepts. ${studentFirstName} has potential to excel.`,
        `${studentFirstName} is making very good progress. Encourage continued effort.`
      ],
      'B': [
        `Solid performance in ${subjectName}. ${studentFirstName} should continue building on this foundation.`,
        `${studentFirstName} demonstrates good grasp of ${subjectName}. More practice will help improve.`,
        `Good work in ${subjectName}. ${studentFirstName} is on the right track.`
      ],
      'B-': [
        `${studentFirstName} shows adequate understanding in ${subjectName}. Room for improvement exists.`,
        `Satisfactory progress in ${subjectName}. ${studentFirstName} should focus on weaker areas.`,
        `${studentFirstName} is progressing well but needs to maintain consistent effort.`
      ],
      'C+': [
        `${studentFirstName} is showing satisfactory progress in ${subjectName}. More revision needed.`,
        `Fair understanding of ${subjectName}. ${studentFirstName} should spend more time on practice.`,
        `${studentFirstName} needs to put in more effort to improve ${subjectName} performance.`
      ],
      'C': [
        `${studentFirstName} demonstrates basic understanding of ${subjectName}. Additional support recommended.`,
        `Satisfactory work but ${studentFirstName} needs to work harder in ${subjectName}.`,
        `${studentFirstName} should seek extra help and practice regularly in ${subjectName}.`
      ],
      'C-': [
        `${studentFirstName} is just passing in ${subjectName}. Significant improvement needed.`,
        `More effort required in ${subjectName}. ${studentFirstName} should attend tutorials.`,
        `${studentFirstName} needs to focus more on ${subjectName} to avoid failing.`
      ],
      'D': [
        `${studentFirstName} is struggling in ${subjectName}. Immediate intervention recommended.`,
        `Below average performance. ${studentFirstName} requires additional support in ${subjectName}.`,
        `${studentFirstName} must work much harder in ${subjectName}. Parent meeting advised.`
      ],
      'F': [
        `${studentFirstName} has not met the minimum requirements in ${subjectName}. Urgent attention needed.`,
        `Failing grade in ${subjectName}. ${studentFirstName} requires immediate remedial support.`,
        `${studentFirstName} must receive intensive support to improve in ${subjectName}.`
      ]
    };
    
    const gradeKey = gradeInfo.grade.replace('-', '-');
    const gradeComments = comments[gradeKey] || comments['C'];
    // Use deterministic selection based on subject name hash
    const hashCode = subjectName.split('').reduce((a, b) => ((a << 5) - a) + b.charCodeAt(0), 0);
    return gradeComments[Math.abs(hashCode) % gradeComments.length];
  };

  // Auto-generate overall class teacher remarks
  const generateClassTeacherRemarks = (overallPercentage, studentFirstName, totalSubjects, stream) => {
    const streamName = stream || 'General';
    
    if (overallPercentage >= 85) {
      return `${studentFirstName} has demonstrated exceptional academic excellence across all ${totalSubjects} ${streamName} stream subjects this term. Their dedication, hard work, and positive attitude towards learning have resulted in outstanding achievements. ${studentFirstName} is a role model for other students and should be very proud of these results.`;
    } else if (overallPercentage >= 70) {
      return `${studentFirstName} has shown very good progress this term in the ${streamName} stream, achieving commendable results across ${totalSubjects} subjects. With continued effort and dedication, even better results are achievable. I encourage ${studentFirstName} to maintain this positive momentum.`;
    } else if (overallPercentage >= 55) {
      return `${studentFirstName} has made satisfactory progress this term in the ${streamName} stream. While demonstrating understanding of basic concepts, there is room for improvement across some subjects. I encourage ${studentFirstName} to seek additional help when needed and to spend more time on revision.`;
    } else if (overallPercentage >= 40) {
      return `${studentFirstName} needs to improve academic performance significantly in the ${streamName} stream. More focus, regular attendance, and consistent study habits are essential. I strongly recommend working closely with subject teachers and establishing a daily study routine.`;
    } else {
      return `${studentFirstName} is facing significant academic challenges in the ${streamName} stream and requires immediate support. A parent-teacher meeting is recommended to discuss strategies for improvement. Extra tutoring and close supervision are essential for success.`;
    }
  };

  // Auto-generate principal remarks
  const generatePrincipalRemarks = (overallPercentage, studentFirstName, totalSubjects, stream) => {
    const streamName = stream || 'General';
    
    if (overallPercentage >= 85) {
      return `Congratulations ${studentFirstName} on your outstanding academic performance this term in the ${streamName} stream! Your exceptional results across ${totalSubjects} subjects reflect your dedication and hard work. Continue to set high standards and be an inspiration to your peers. Well done!`;
    } else if (overallPercentage >= 70) {
      return `Well done ${studentFirstName} on achieving good results this term in the ${streamName} stream. Your performance shows that you have the ability to succeed. I encourage you to continue working hard and aim even higher next term.`;
    } else if (overallPercentage >= 55) {
      return `${studentFirstName}, while your performance in the ${streamName} stream is satisfactory, I believe you can achieve more. I encourage you to set higher goals, seek help from teachers when needed, and dedicate more time to your studies. You have the potential to improve significantly.`;
    } else if (overallPercentage >= 40) {
      return `${studentFirstName}, improvement is urgently needed in your ${streamName} stream academic performance. Please take your studies more seriously, attend all classes, and complete all assignments. Work closely with your teachers and parents to develop better study habits.`;
    } else {
      return `${studentFirstName}, we are very concerned about your academic performance in the ${streamName} stream. Immediate action is required to prevent further decline. Please meet with your class teacher and work with your parents to create an improvement plan. The school is here to support you.`;
    }
  };

  // Get student's first name for personalized comments
  const studentFirstName = student.name?.split(' ')[0] || 'Student';

  // Get stream definition
  const streamDef = STREAM_SUBJECTS[selectedStream];
  
  // Filter grades based on stream and active subjects
  const getFilteredGrades = () => {
    if (!streamDef) return grades;
    
    const validSubjects = [...streamDef.core];
    if (selectedOptional) {
      validSubjects.push(selectedOptional);
    }
    
    return grades.filter(g => {
      // Check if subject is in stream OR is the selected optional
      const isInStream = validSubjects.includes(g.name);
      const isActive = activeSubjects[g.name] !== false;
      return isInStream && isActive;
    });
  };

  // Process grades data - ensure each subject has proper structure
  const filteredGrades = getFilteredGrades();
  const processedSubjects = filteredGrades.map(subject => {
    const ca = subject.ca || 0;
    const exam = subject.exam || 0;
    const total = subject.total || (ca + exam);
    const gradeInfo = calculateGrade(total);
    
    return {
      ...subject,
      ca,
      exam,
      total,
      grade: subject.grade || gradeInfo.grade,
      gpa: subject.gpa || gradeInfo.gpa,
      pointScale: getPointScale(total),
      teacherComment: subject.teacherComment || generateSubjectComment(subject.name, total, studentFirstName),
      isCore: streamDef?.core.includes(subject.name),
      isOptional: streamDef?.optional.includes(subject.name)
    };
  });

  // Calculate overall statistics
  const totalScores = processedSubjects.filter(s => s.total > 0);
  const overallAverage = totalScores.length > 0 
    ? Math.round(totalScores.reduce((sum, s) => sum + s.total, 0) / totalScores.length)
    : 0;
  const overallGrade = calculateGrade(overallAverage);

  // Calculate average point scale
  const avgPointScale = totalScores.length > 0
    ? (totalScores.reduce((sum, s) => sum + parseFloat(s.pointScale || getPointScale(s.total)), 0) / totalScores.length).toFixed(2)
    : '0.00';

  // Auto-generate comments if not provided
  const finalClassTeacherComment = classTeacherComment || generateClassTeacherRemarks(overallAverage, studentFirstName, totalScores.length, selectedStream);
  const finalPrincipalComment = principalComment || generatePrincipalRemarks(overallAverage, studentFirstName, totalScores.length, selectedStream);

  // Grade color helper
  const getGradeColorClass = (grade) => {
    if (grade.startsWith('A')) return 'bg-green-100 text-green-800 border-green-300';
    if (grade.startsWith('B')) return 'bg-blue-100 text-blue-800 border-blue-300';
    if (grade.startsWith('C')) return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    if (grade === 'D') return 'bg-orange-100 text-orange-800 border-orange-300';
    return 'bg-red-100 text-red-800 border-red-300';
  };

  // Handle subject toggle
  const handleSubjectToggle = (subjectName, isActive) => {
    setActiveSubjects(prev => ({
      ...prev,
      [subjectName]: isActive
    }));
    
    if (onSubjectToggle) {
      onSubjectToggle(student.id, subjectName, studentYear, isActive);
    }
  };

  // Handle stream change
  const handleStreamChange = (newStream) => {
    setSelectedStream(newStream);
    setSelectedOptional(''); // Reset optional when stream changes
    
    if (onStreamChange) {
      onStreamChange(student.id, newStream, studentYear);
    }
  };

  // Handle optional subject selection
  const handleOptionalChange = (subject) => {
    setSelectedOptional(subject);
  };

  const StreamIcon = streamDef?.icon || BookOpen;

  const printReportCard = () => {
    const printWindow = window.open('', '_blank');
    
    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Progress Report - ${student.name}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: Arial, sans-serif; 
            padding: 15px; 
            max-width: 900px; 
            margin: 0 auto;
            font-size: 11px;
            line-height: 1.4;
            background: white;
          }
          
          /* Header */
          .header-container {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            border-bottom: 3px solid #1e40af;
            padding-bottom: 15px;
            margin-bottom: 15px;
          }
          .header-left { flex: 1; }
          .header-center { flex: 2; text-align: center; }
          .header-right { flex: 1; text-align: right; font-size: 10px; }
          .report-title { font-size: 18px; font-weight: bold; color: #1e40af; margin-bottom: 5px; }
          .term-year { font-size: 14px; font-weight: bold; color: #374151; }
          .school-name { font-size: 20px; font-weight: bold; color: #1e40af; }
          .school-logo { width: 70px; height: 70px; margin: 0 auto 8px; }
          .stream-badge {
            display: inline-block;
            padding: 4px 12px;
            border-radius: 20px;
            font-weight: bold;
            font-size: 12px;
            margin-top: 8px;
            background: ${selectedStream === 'Science' ? '#f3e8ff' : selectedStream === 'Commerce' ? '#dcfce7' : '#fef3c7'};
            color: ${selectedStream === 'Science' ? '#7c3aed' : selectedStream === 'Commerce' ? '#16a34a' : '#d97706'};
          }
          
          /* Student Info */
          .student-info {
            display: grid;
            grid-template-columns: repeat(5, 1fr);
            gap: 15px;
            margin-bottom: 15px;
            padding: 12px;
            background: linear-gradient(to right, #eff6ff, #eef2ff);
            border-radius: 8px;
            border: 1px solid #bfdbfe;
          }
          .student-info-item { display: flex; flex-direction: column; }
          .student-info-item label { font-size: 9px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.5px; }
          .student-info-item span { font-weight: bold; font-size: 12px; color: #1f2937; }
          
          /* Main grades table */
          .grades-table { width: 100%; border-collapse: collapse; margin-bottom: 15px; }
          .grades-table th {
            background: linear-gradient(to bottom, #1e40af, #1e3a8a);
            color: white;
            border: 1px solid #1e3a8a;
            padding: 8px 4px;
            font-size: 10px;
            text-align: center;
            font-weight: bold;
          }
          .grades-table th:first-child { text-align: left; width: 110px; }
          .grades-table th:last-child { text-align: left; width: 200px; }
          .grades-table td {
            border: 1px solid #d1d5db;
            padding: 6px 4px;
            text-align: center;
            font-size: 10px;
          }
          .grades-table td:first-child { text-align: left; font-weight: 600; background: #f9fafb; }
          .grades-table td:last-child { text-align: left; font-size: 9px; padding: 6px 8px; }
          .grades-table tbody tr:nth-child(even) { background-color: #f9fafb; }
          
          /* Grade badges */
          .grade-badge { display: inline-block; padding: 2px 8px; border-radius: 4px; font-weight: bold; font-size: 10px; }
          .grade-a { background: #dcfce7; color: #166534; }
          .grade-b { background: #dbeafe; color: #1e40af; }
          .grade-c { background: #fef9c3; color: #854d0e; }
          .grade-d { background: #fed7aa; color: #c2410c; }
          .grade-f { background: #fecaca; color: #dc2626; }
          .subject-type { font-size: 8px; color: #6b7280; display: block; }
          
          /* Summary section */
          .summary-section { display: flex; gap: 20px; margin-bottom: 15px; }
          .grade-scale {
            border: 1px solid #d1d5db;
            padding: 10px;
            font-size: 9px;
            border-radius: 6px;
            background: #f9fafb;
            width: 180px;
          }
          .grade-scale-title { font-weight: bold; text-align: center; margin-bottom: 8px; background: #e5e7eb; padding: 4px; border-radius: 4px; }
          .grade-row { display: flex; justify-content: space-between; padding: 2px 4px; border-bottom: 1px dotted #d1d5db; }
          .overall-summary { flex: 1; border: 2px solid #1e40af; border-radius: 8px; overflow: hidden; }
          .overall-summary-header { background: linear-gradient(to right, #1e40af, #3b82f6); color: white; padding: 8px 12px; font-weight: bold; text-align: center; }
          .overall-summary-content { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; padding: 12px; }
          .summary-stat { text-align: center; }
          .summary-stat-value { font-size: 20px; font-weight: bold; color: #1e40af; }
          .summary-stat-label { font-size: 9px; color: #6b7280; text-transform: uppercase; }
          
          /* Comments section */
          .comment-section { margin-bottom: 12px; }
          .comment-label { font-weight: bold; font-size: 11px; margin-bottom: 4px; color: #1e40af; }
          .comment-box { border: 1px solid #d1d5db; min-height: 50px; padding: 10px; font-size: 10px; line-height: 1.5; background: #fafafa; border-radius: 4px; }
          .comment-signature { text-align: right; font-style: italic; color: #6b7280; margin-top: 5px; font-size: 9px; }
          
          /* Signatures */
          .signatures { display: flex; justify-content: space-between; margin-top: 25px; padding-top: 15px; }
          .signature-box { text-align: center; width: 140px; }
          .signature-line { border-top: 1px solid #374151; margin-top: 35px; padding-top: 5px; font-size: 10px; font-weight: 500; }
          
          .footer { text-align: center; margin-top: 20px; font-size: 9px; color: #6b7280; border-top: 1px solid #e5e7eb; padding-top: 10px; }
          
          @media print { body { padding: 10px; } .no-print { display: none; } }
        </style>
      </head>
      <body>
        <!-- Header -->
        <div class="header-container">
          <div class="header-left">
            <div class="report-title">PROGRESS REPORT</div>
            <div class="term-year">${term.toUpperCase()} ${year}</div>
            <div class="stream-badge">${selectedStream} Stream</div>
          </div>
          <div class="header-center">
            ${logoUrl ? `<img src="${logoUrl}" class="school-logo" alt="School Logo" style="width:70px;height:70px;object-fit:contain;"/>` : 
              `<div style="width:70px;height:70px;border:2px solid #1e40af;border-radius:50%;display:flex;align-items:center;justify-content:center;margin:0 auto 8px;font-size:24px;background:#eff6ff;">🏫</div>`}
            <div class="school-name">${schoolName}</div>
          </div>
          <div class="header-right">
            <div>${schoolAddress.poBox}</div>
            <div>${schoolAddress.location}</div>
            <div>${schoolAddress.country}</div>
            <div>Email: ${schoolAddress.email}</div>
          </div>
        </div>
        
        <!-- Student Info -->
        <div class="student-info">
          <div class="student-info-item">
            <label>Student Name</label>
            <span>${student.name}</span>
          </div>
          <div class="student-info-item">
            <label>Student ID</label>
            <span>${student.birth_registration || student.student_id || student.id || 'N/A'}</span>
          </div>
          <div class="student-info-item">
            <label>Class</label>
            <span>Year ${studentYear}</span>
          </div>
          <div class="student-info-item">
            <label>Stream</label>
            <span>${selectedStream}</span>
          </div>
          <div class="student-info-item">
            <label>Term</label>
            <span>${term}</span>
          </div>
        </div>
        
        <!-- Grades Table -->
        <table class="grades-table">
          <thead>
            <tr>
              <th>Subject</th>
              <th>CA (40%)</th>
              <th>Exam (60%)</th>
              <th>Total (100%)</th>
              <th>Class Avg</th>
              <th>Ranking</th>
              <th>Grade</th>
              <th>Absences</th>
              <th>Teacher's Comments</th>
            </tr>
          </thead>
          <tbody>
            ${processedSubjects.map(subject => {
              const gradeClass = subject.grade.startsWith('A') ? 'grade-a' : 
                                 subject.grade.startsWith('B') ? 'grade-b' : 
                                 subject.grade.startsWith('C') ? 'grade-c' :
                                 subject.grade === 'D' ? 'grade-d' : 'grade-f';
              const subjectType = subject.isCore ? 'Core' : 'Optional';
              return `
                <tr>
                  <td>${subject.name}<span class="subject-type">(${subjectType})</span></td>
                  <td>${subject.ca || '-'}</td>
                  <td>${subject.exam || '-'}</td>
                  <td><strong>${subject.total || '-'}</strong></td>
                  <td>${subject.classAvg || '-'}</td>
                  <td>${subject.ranking || '-'}</td>
                  <td><span class="grade-badge ${gradeClass}">${subject.grade}</span></td>
                  <td>${subject.absences || '0'}</td>
                  <td>${subject.teacherComment}</td>
                </tr>
              `;
            }).join('')}
          </tbody>
        </table>
        
        <!-- Summary Section -->
        <div class="summary-section">
          <div class="grade-scale">
            <div class="grade-scale-title">Grading Scale</div>
            <div class="grade-row"><span>90-100%</span><span>A+ (4.5)</span></div>
            <div class="grade-row"><span>85-89%</span><span>A (4.0)</span></div>
            <div class="grade-row"><span>80-84%</span><span>A- (3.7)</span></div>
            <div class="grade-row"><span>75-79%</span><span>B+ (3.5)</span></div>
            <div class="grade-row"><span>70-74%</span><span>B (3.0)</span></div>
            <div class="grade-row"><span>65-69%</span><span>B- (2.7)</span></div>
            <div class="grade-row"><span>60-64%</span><span>C+ (2.5)</span></div>
            <div class="grade-row"><span>55-59%</span><span>C (2.0)</span></div>
            <div class="grade-row"><span>50-54%</span><span>C- (1.7)</span></div>
            <div class="grade-row"><span>40-49%</span><span>D (1.0)</span></div>
            <div class="grade-row"><span>Below 40%</span><span>F (0.0)</span></div>
          </div>
          
          <div class="overall-summary">
            <div class="overall-summary-header">${selectedStream.toUpperCase()} STREAM - OVERALL PERFORMANCE</div>
            <div class="overall-summary-content">
              <div class="summary-stat">
                <div class="summary-stat-value">${totalScores.length}</div>
                <div class="summary-stat-label">Subjects</div>
              </div>
              <div class="summary-stat">
                <div class="summary-stat-value">${overallAverage}%</div>
                <div class="summary-stat-label">Average</div>
              </div>
              <div class="summary-stat">
                <div class="summary-stat-value">${overallGrade.grade}</div>
                <div class="summary-stat-label">Grade</div>
              </div>
              <div class="summary-stat">
                <div class="summary-stat-value">${avgPointScale}</div>
                <div class="summary-stat-label">GPA</div>
              </div>
            </div>
            <div style="text-align:center;padding:8px;background:#eff6ff;font-weight:bold;color:#1e40af;">
              ${overallGrade.description}
            </div>
          </div>
        </div>
        
        <!-- Attendance Summary -->
        <div style="margin-top:15px;padding:12px;background:#f0fdf4;border:1px solid #86efac;border-radius:8px;">
          <div style="font-weight:bold;color:#166534;margin-bottom:8px;font-size:11px;">ATTENDANCE RECORD</div>
          <div style="display:flex;justify-content:space-around;text-align:center;">
            <div>
              <div style="font-size:16px;font-weight:bold;color:#166534;">${attendance?.present || 0}</div>
              <div style="font-size:9px;color:#6b7280;">Days Present</div>
            </div>
            <div>
              <div style="font-size:16px;font-weight:bold;color:#dc2626;">${attendance?.absent || 0}</div>
              <div style="font-size:9px;color:#6b7280;">Days Absent</div>
            </div>
            <div>
              <div style="font-size:16px;font-weight:bold;color:#f59e0b;">${attendance?.late || 0}</div>
              <div style="font-size:9px;color:#6b7280;">Days Late</div>
            </div>
            <div>
              <div style="font-size:16px;font-weight:bold;color:#3b82f6;">${attendance?.excused || 0}</div>
              <div style="font-size:9px;color:#6b7280;">Excused</div>
            </div>
            <div>
              <div style="font-size:16px;font-weight:bold;color:#059669;">${attendance?.attendance_rate || 0}%</div>
              <div style="font-size:9px;color:#6b7280;">Attendance Rate</div>
            </div>
          </div>
        </div>
        
        <!-- Comments Section -->
        <div class="comment-section">
          <div class="comment-label">Class Teacher's Comment:</div>
          <div class="comment-box">
            ${finalClassTeacherComment}
            <div class="comment-signature">— Class Teacher</div>
          </div>
        </div>
        
        <div class="comment-section">
          <div class="comment-label">Principal's Comment:</div>
          <div class="comment-box">
            ${finalPrincipalComment}
            <div class="comment-signature">— Principal</div>
          </div>
        </div>
        
        <!-- Signatures - Only Principal -->
        <div class="signatures" style="justify-content: center;">
          <div class="signature-box" style="width: 200px;">
            <div class="signature-line">Principal</div>
          </div>
        </div>
        
        <div class="footer">
          <p>This progress report was generated by Penama Education Portal</p>
          <p>Date Issued: ${new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
        </div>
      </body>
      </html>
    `;
    
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.onload = () => printWindow.print();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="secondary-report-card-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <FileText className="w-6 h-6 text-blue-600" />
            Year {studentYear} Progress Report
            <Badge className={streamDef?.color || 'bg-gray-100'}>
              <StreamIcon className="w-3 h-3 mr-1" />
              {selectedStream} Stream
            </Badge>
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-500">
            Student term report with stream-based subjects, grades, GPA, and auto-generated comments
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Settings Toggle */}
          <div className="flex justify-end">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setShowSettings(!showSettings)}
              data-testid="toggle-settings-btn"
            >
              <Settings className="w-4 h-4 mr-2" />
              {showSettings ? 'Hide' : 'Customize'} Subjects
            </Button>
          </div>

          {/* Settings Panel */}
          {showSettings && (
            <div className="bg-gray-50 p-4 rounded-lg border space-y-4" data-testid="settings-panel">
              <h4 className="font-semibold text-sm">Subject Settings</h4>
              
              {/* Stream Selection */}
              <div className="flex items-center gap-4">
                <span className="text-sm font-medium">Stream:</span>
                <Select value={selectedStream} onValueChange={handleStreamChange}>
                  <SelectTrigger className="w-40" data-testid="stream-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Science">
                      <div className="flex items-center gap-2">
                        <Beaker className="w-4 h-4" />
                        Science
                      </div>
                    </SelectItem>
                    <SelectItem value="Commerce">
                      <div className="flex items-center gap-2">
                        <Calculator className="w-4 h-4" />
                        Commerce
                      </div>
                    </SelectItem>
                    <SelectItem value="Arts">
                      <div className="flex items-center gap-2">
                        <BookOpen className="w-4 h-4" />
                        Arts
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Optional Subject Selection */}
              {streamDef && (
                <div className="flex items-center gap-4">
                  <span className="text-sm font-medium">Optional Subject:</span>
                  <Select value={selectedOptional} onValueChange={handleOptionalChange}>
                    <SelectTrigger className="w-40" data-testid="optional-select">
                      <SelectValue placeholder="Select optional" />
                    </SelectTrigger>
                    <SelectContent>
                      {streamDef.optional.map(subj => (
                        <SelectItem key={subj} value={subj}>{subj}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Subject Toggles */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {streamDef && [...streamDef.core, ...(selectedOptional ? [selectedOptional] : [])].map(subject => (
                  <div key={subject} className="flex items-center justify-between p-2 bg-white rounded border">
                    <span className="text-sm">{subject}</span>
                    <div className="flex items-center gap-2">
                      {streamDef.core.includes(subject) ? (
                        <Badge variant="outline" className="text-xs">Core</Badge>
                      ) : (
                        <Badge variant="secondary" className="text-xs">Optional</Badge>
                      )}
                      <Switch
                        checked={activeSubjects[subject] !== false}
                        onCheckedChange={(checked) => handleSubjectToggle(subject, checked)}
                        data-testid={`toggle-${subject.toLowerCase().replace(' ', '-')}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* School Header */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-lg font-bold text-blue-800">PROGRESS REPORT</h2>
                <p className="text-blue-600">{term.toUpperCase()} {year}</p>
                <Badge className={`mt-2 ${streamDef?.color || 'bg-gray-100'}`}>
                  <StreamIcon className="w-3 h-3 mr-1" />
                  {selectedStream} Stream
                </Badge>
              </div>
              <div className="text-center">
                {logoUrl ? (
                  <img 
                    src={logoUrl} 
                    alt={`${schoolName} Logo`}
                    className="w-16 h-16 object-contain mx-auto mb-2"
                  />
                ) : (
                  <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2">
                    <School className="w-7 h-7 text-blue-600" />
                  </div>
                )}
                <h3 className="font-bold text-blue-800">{schoolName}</h3>
              </div>
              <div className="text-right text-sm text-blue-600">
                <p>{schoolAddress.poBox}</p>
                <p>{schoolAddress.location}</p>
                <p>{schoolAddress.country}</p>
                <p>{schoolAddress.email}</p>
              </div>
            </div>
          </div>

          {/* Student Info */}
          <div className="bg-gray-50 p-4 rounded-lg border grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="flex items-center gap-2">
              <User className="w-5 h-5 text-blue-600" />
              <div>
                <p className="text-xs text-gray-500">Student Name</p>
                <p className="font-semibold">{student.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-blue-600" />
              <div>
                <p className="text-xs text-gray-500">Class</p>
                <p className="font-semibold">Year {studentYear}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <StreamIcon className="w-5 h-5 text-blue-600" />
              <div>
                <p className="text-xs text-gray-500">Stream</p>
                <p className="font-semibold">{selectedStream}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <School className="w-5 h-5 text-blue-600" />
              <div>
                <p className="text-xs text-gray-500">Student ID</p>
                <p className="font-semibold">{student.birth_registration || student.student_id || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-600" />
              <div>
                <p className="text-xs text-gray-500">Term</p>
                <p className="font-semibold">{term}</p>
              </div>
            </div>
          </div>

          {/* Grades Table */}
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-blue-700 text-white">
                <tr>
                  <th className="text-left p-3 w-28">Subject</th>
                  <th className="text-center p-3 w-16">CA (40%)</th>
                  <th className="text-center p-3 w-16">Exam (60%)</th>
                  <th className="text-center p-3 w-16">Total</th>
                  <th className="text-center p-3 w-16">Class Avg</th>
                  <th className="text-center p-3 w-14">Rank</th>
                  <th className="text-center p-3 w-14">Grade</th>
                  <th className="text-center p-3 w-14">Absences</th>
                  <th className="text-left p-3">Teacher&apos;s Comments</th>
                </tr>
              </thead>
              <tbody>
                {processedSubjects.map((subject, idx) => (
                  <tr key={idx} className={`border-b hover:bg-blue-50 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                    <td className="p-3">
                      <span className="font-bold">{subject.name}</span>
                      <span className="block text-xs text-gray-500">
                        {subject.isCore ? '(Core)' : '(Optional)'}
                      </span>
                    </td>
                    <td className="text-center p-3">{subject.ca || '-'}</td>
                    <td className="text-center p-3">{subject.exam || '-'}</td>
                    <td className="text-center p-3 font-bold">{subject.total || '-'}</td>
                    <td className="text-center p-3 text-gray-600">{subject.classAvg || '-'}</td>
                    <td className="text-center p-3 font-medium text-amber-700">{subject.ranking || '-'}</td>
                    <td className="text-center p-3">
                      <span className={`px-2 py-1 rounded text-xs font-bold border ${getGradeColorClass(subject.grade)}`}>
                        {subject.grade}
                      </span>
                    </td>
                    <td className="text-center p-3">{subject.absences || '0'}</td>
                    <td className="p-3 text-xs text-gray-600">{subject.teacherComment}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Summary Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Grade Scale */}
            <div className="border rounded-lg overflow-hidden">
              <div className="bg-gray-100 p-2 font-bold text-sm text-center">Grading Scale</div>
              <div className="p-3 text-xs grid grid-cols-2 gap-1">
                <div className="flex justify-between"><span>90-100%</span><span className="font-bold">A+ (4.5)</span></div>
                <div className="flex justify-between"><span>85-89%</span><span className="font-bold">A (4.0)</span></div>
                <div className="flex justify-between"><span>80-84%</span><span className="font-bold">A- (3.7)</span></div>
                <div className="flex justify-between"><span>75-79%</span><span className="font-bold">B+ (3.5)</span></div>
                <div className="flex justify-between"><span>70-74%</span><span className="font-bold">B (3.0)</span></div>
                <div className="flex justify-between"><span>65-69%</span><span className="font-bold">B- (2.7)</span></div>
                <div className="flex justify-between"><span>60-64%</span><span className="font-bold">C+ (2.5)</span></div>
                <div className="flex justify-between"><span>55-59%</span><span className="font-bold">C (2.0)</span></div>
                <div className="flex justify-between"><span>50-54%</span><span className="font-bold">C- (1.7)</span></div>
                <div className="flex justify-between"><span>40-49%</span><span className="font-bold">D (1.0)</span></div>
                <div className="flex justify-between col-span-2"><span>Below 40%</span><span className="font-bold text-red-600">F (0.0)</span></div>
              </div>
              <div className="bg-blue-100 p-3 text-center">
                <span className="text-sm font-bold text-blue-800">Avg Point Scale: {avgPointScale}</span>
              </div>
            </div>

            {/* Overall Summary */}
            <div className="border-2 border-blue-600 rounded-lg overflow-hidden">
              <div className={`text-white p-2 font-bold text-sm text-center ${
                selectedStream === 'Science' ? 'bg-gradient-to-r from-purple-600 to-purple-700' :
                selectedStream === 'Commerce' ? 'bg-gradient-to-r from-green-600 to-green-700' :
                'bg-gradient-to-r from-amber-600 to-amber-700'
              }`}>
                {selectedStream.toUpperCase()} STREAM - OVERALL PERFORMANCE
              </div>
              <div className="p-4 grid grid-cols-4 gap-2 text-center">
                <div>
                  <p className="text-2xl font-bold text-blue-700">{totalScores.length}</p>
                  <p className="text-xs text-gray-500">Subjects</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-700">{overallAverage}%</p>
                  <p className="text-xs text-gray-500">Average</p>
                </div>
                <div>
                  <span className={`text-xl font-bold px-2 py-1 rounded ${getGradeColorClass(overallGrade.grade)}`}>
                    {overallGrade.grade}
                  </span>
                  <p className="text-xs text-gray-500 mt-1">Grade</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-700">{avgPointScale}</p>
                  <p className="text-xs text-gray-500">GPA</p>
                </div>
              </div>
              <div className={`text-center p-2 font-bold ${getGradeColorClass(overallGrade.grade)}`}>
                {overallGrade.description}
              </div>
            </div>
          </div>

          {/* Class Teacher Remarks - Auto Generated */}
          <div className="border rounded-lg overflow-hidden">
            <div className="bg-blue-700 text-white p-2 font-bold text-sm flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Class Teacher&apos;s Remarks (Auto-Generated)
            </div>
            <div className="p-4 text-sm text-gray-700 leading-relaxed">
              {finalClassTeacherComment}
              <p className="text-right mt-2 text-gray-500 italic text-xs">— Class Teacher</p>
            </div>
          </div>

          {/* Principal Remarks - Auto Generated */}
          <div className="border rounded-lg overflow-hidden">
            <div className="bg-blue-700 text-white p-2 font-bold text-sm flex items-center gap-2">
              <Award className="w-4 h-4" />
              Principal&apos;s Remarks (Auto-Generated)
            </div>
            <div className="p-4 text-sm text-gray-700 leading-relaxed">
              {finalPrincipalComment}
              <p className="text-right mt-2 text-gray-500 italic text-xs">— Principal</p>
            </div>
          </div>

          {/* Overall Summary Card */}
          <div className={`p-4 rounded-lg border ${
            selectedStream === 'Science' ? 'bg-gradient-to-r from-purple-50 to-purple-100 border-purple-200' :
            selectedStream === 'Commerce' ? 'bg-gradient-to-r from-green-50 to-green-100 border-green-200' :
            'bg-gradient-to-r from-amber-50 to-amber-100 border-amber-200'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Award className={`w-10 h-10 ${
                  overallGrade.grade.startsWith('A') ? 'text-green-600' :
                  overallGrade.grade.startsWith('B') ? 'text-blue-600' :
                  overallGrade.grade.startsWith('C') ? 'text-yellow-600' :
                  'text-red-600'
                }`} />
                <div>
                  <p className="text-sm text-gray-600">{selectedStream} Stream - Overall Performance</p>
                  <p className="text-xl font-bold text-blue-800">{overallGrade.description}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-blue-700">{avgPointScale}</p>
                <p className="text-sm text-gray-500">GPA</p>
              </div>
            </div>
          </div>

          {/* Print Button */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={onClose} data-testid="close-secondary-report-btn">
              <X className="w-4 h-4 mr-2" />
              Close
            </Button>
            <Button onClick={printReportCard} className="bg-blue-600 hover:bg-blue-700" data-testid="print-secondary-report-btn">
              <Printer className="w-4 h-4 mr-2" />
              Print Report Card
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
